import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Users, Wallet, TrendingUp, Shield, MessageSquare, Building2, Activity, Download, Search, Ban, RotateCcw, Crown, ExternalLink } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const A = `${BACKEND_URL}/api/admin`;

/** Small helper: money format */
const $ = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso || '—'; } };

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${A}/me`, { withCredentials: true });
        setIsAdmin(!!r.data?.is_admin);
      } catch (e) { setIsAdmin(false); }
      finally { setChecking(false); }
    })();
  }, []);

  if (checking) return <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center">Loading…</div>;
  if (!isAdmin) return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex flex-col items-center justify-center gap-4">
      <Shield className="w-12 h-12 text-rose-500" />
      <div className="text-xl font-black">Admin access required</div>
      <Button onClick={() => navigate('/')} className="bg-lime-500 text-black">Back to home</Button>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white p-4 md:p-8" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl md:text-4xl font-black uppercase tracking-tight" style={{ fontFamily: 'Unbounded, sans-serif' }}>
              Admin Command Center
            </h1>
            <p className="text-sm text-neutral-400 mt-1">Every user, transaction, and safety flag in one place.</p>
          </div>
          <Button variant="outline" onClick={() => navigate('/dashboard')} className="border-white/10 text-white hover:bg-white/10">
            Exit admin →
          </Button>
        </div>

        <Tabs defaultValue="overview">
          <TabsList className="bg-white/5 border border-white/10 mb-6 flex-wrap h-auto">
            {[
              ['overview',   'Overview',    Activity],
              ['users',      'Members',     Users],
              ['transactions','Transactions',Wallet],
              ['memberships','Memberships', Crown],
              ['clubs',      'Clubs',       Users],
              ['moderation', 'Moderation',  MessageSquare],
              ['plaid',      'Plaid',       Building2],
              ['audit',      'Audit log',   Shield],
            ].map(([k, label, Icon]) => (
              <TabsTrigger key={k} value={k} className="data-[state=active]:bg-lime-500 data-[state=active]:text-black" data-testid={`admin-tab-${k}`}>
                <Icon className="w-4 h-4 mr-1.5" />{label}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="overview"><OverviewTab /></TabsContent>
          <TabsContent value="users"><UsersTab /></TabsContent>
          <TabsContent value="transactions"><TransactionsTab /></TabsContent>
          <TabsContent value="memberships"><MembershipsTab /></TabsContent>
          <TabsContent value="clubs"><ClubsTab /></TabsContent>
          <TabsContent value="moderation"><ModerationTab /></TabsContent>
          <TabsContent value="plaid"><PlaidTab /></TabsContent>
          <TabsContent value="audit"><AuditTab /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

// ---------------- Sub-tabs ----------------- //
const Stat = ({ label, value, hint, tint = 'text-lime-400' }) => (
  <Card className="bg-white/[0.03] border-white/10 text-white">
    <CardContent className="p-5">
      <div className="text-xs uppercase tracking-widest text-neutral-500 font-bold">{label}</div>
      <div className={`text-3xl font-black mt-2 ${tint}`} style={{ fontFamily: 'Unbounded, sans-serif' }}>{value}</div>
      {hint && <div className="text-xs text-neutral-500 mt-1">{hint}</div>}
    </CardContent>
  </Card>
);

function OverviewTab() {
  const [d, setD] = useState(null);
  useEffect(() => { axios.get(`${A}/overview`, { withCredentials: true }).then(r => setD(r.data)); }, []);
  if (!d) return <div className="text-neutral-500">Loading…</div>;
  return (
    <div className="space-y-6" data-testid="admin-overview">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Total members"  value={d.users.total} hint={`${d.users.active_30d} active last 30d`} />
        <Stat label="Bank verified"  value={d.users.bank_verified} hint="Plaid identity-matched" tint="text-emerald-400" />
        <Stat label="Active clubs"   value={d.clubs.active} hint={`${d.clubs.total} total`} tint="text-cyan-400" />
        <Stat label="Contributions"  value={d.money.total_contributions_count} hint={$(d.money.total_contribution_volume) + ' volume'} tint="text-amber-400" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="MRR"            value={$(d.revenue.mrr_usd)} hint={$(d.revenue.arr_usd) + ' ARR'} tint="text-lime-400" />
        <Stat label="Premium"        value={d.users.premium} hint="$20/yr tier" tint="text-yellow-400" />
        <Stat label="Pro"            value={d.users.pro} hint="$50/yr tier" tint="text-violet-400" />
        <Stat label="Flags open"     value={d.moderation.flags_open} hint="pending review" tint={d.moderation.flags_open ? 'text-rose-400' : 'text-neutral-500'} />
      </div>
      <div className="text-xs text-neutral-600">Generated at {fmtDate(d.generated_at)}</div>
    </div>
  );
}

function UsersTab() {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const load = useCallback(async () => {
    const r = await axios.get(`${A}/users`, { params: { search: q, limit: 50 }, withCredentials: true });
    setRows(r.data.users); setTotal(r.data.total);
  }, [q]);
  useEffect(() => { load(); }, [load]);

  const suspend = async (u) => {
    const action = u.is_suspended ? 'unsuspend' : 'suspend';
    await axios.post(`${A}/users/${u.user_id}/${action}`, {}, { withCredentials: true });
    toast.success(`User ${action}ed`);
    load();
  };

  return (
    <div data-testid="admin-users">
      <div className="flex gap-2 mb-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search email / name / nickname…" className="pl-9 bg-white/5 border-white/10 text-white" data-testid="admin-users-search" />
        </div>
        <div className="text-xs text-neutral-500 self-center">{total} total</div>
      </div>
      <div className="rounded-xl border border-white/10 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white/5 text-neutral-400 uppercase text-[10px] tracking-widest">
            <tr>
              <th className="text-left px-3 py-2">Member</th>
              <th className="text-left px-3 py-2">Tier</th>
              <th className="text-left px-3 py-2">Bank</th>
              <th className="text-right px-3 py-2">Savings</th>
              <th className="text-left px-3 py-2">Joined</th>
              <th className="text-right px-3 py-2">Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((u) => (
              <tr key={u.user_id} className="border-t border-white/5 hover:bg-white/[0.02]">
                <td className="px-3 py-2">
                  <div className="font-semibold">{u.name || u.nickname || '—'}</div>
                  <div className="text-[11px] text-neutral-500">{u.email}</div>
                </td>
                <td className="px-3 py-2 capitalize">
                  <Badge className={u.membership_tier === 'pro' ? 'bg-violet-500/20 text-violet-300' : u.membership_tier === 'premium' ? 'bg-amber-500/20 text-amber-300' : 'bg-neutral-700 text-neutral-300'}>{u.membership_tier || 'free'}</Badge>
                </td>
                <td className="px-3 py-2">{u.is_bank_verified ? <span className="text-emerald-400 text-xs">✓ Verified</span> : <span className="text-neutral-500 text-xs">—</span>}</td>
                <td className="px-3 py-2 text-right font-mono">{$(u.total_savings)}</td>
                <td className="px-3 py-2 text-[11px] text-neutral-500">{fmtDate(u.created_at)}</td>
                <td className="px-3 py-2 text-right">
                  <Button size="sm" variant="outline" onClick={() => suspend(u)} className={`border-white/10 ${u.is_suspended ? 'text-emerald-400' : 'text-rose-400'} hover:bg-white/10`} data-testid={`admin-suspend-${u.user_id}`}>
                    {u.is_suspended ? <><RotateCcw className="w-3 h-3 mr-1" />Unsuspend</> : <><Ban className="w-3 h-3 mr-1" />Suspend</>}
                  </Button>
                </td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={6} className="text-center text-neutral-500 py-6">No members</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TransactionsTab() {
  const KIND_MAP = { contributions: 'contribution', bank_transfers: 'bank_transfer', membership_transactions: 'membership' };
  const [kind, setKind] = useState('contributions');
  const [rows, setRows] = useState([]);
  useEffect(() => {
    axios.get(`${A}/transactions`, { params: { kind: KIND_MAP[kind] }, withCredentials: true })
      .then(r => setRows(r.data.items));
  }, [kind]);
  const download = () => window.open(`${A}/transactions/export.csv?kind=${kind}`, '_blank');
  return (
    <div data-testid="admin-transactions">
      <div className="flex gap-2 mb-4 items-center">
        {['contributions', 'bank_transfers', 'membership_transactions'].map((k) => (
          <button key={k} onClick={() => setKind(k)} className={`px-3 py-1.5 rounded-full text-xs font-bold ${kind === k ? 'bg-lime-500 text-black' : 'bg-white/5 text-neutral-400'}`} data-testid={`admin-tx-tab-${k}`}>
            {k.replace(/_/g, ' ')}
          </button>
        ))}
        <div className="flex-1"></div>
        <Button size="sm" onClick={download} className="bg-lime-500 hover:bg-lime-400 text-black" data-testid="admin-tx-export">
          <Download className="w-4 h-4 mr-1" />Export CSV
        </Button>
      </div>
      <div className="rounded-xl border border-white/10 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white/5 text-neutral-400 uppercase text-[10px] tracking-widest">
            <tr>
              <th className="text-left px-3 py-2">When</th>
              <th className="text-left px-3 py-2">User</th>
              <th className="text-right px-3 py-2">Amount</th>
              <th className="text-left px-3 py-2">Details</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-white/5">
                <td className="px-3 py-2 text-[11px] text-neutral-500">{fmtDate(r.created_at || r.timestamp)}</td>
                <td className="px-3 py-2 text-xs">{r.user_id || r.email || '—'}</td>
                <td className="px-3 py-2 text-right font-mono">{$(r.amount)}</td>
                <td className="px-3 py-2 text-[11px] text-neutral-400">{r.club_id || r.description || r.status || ''}</td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={4} className="text-center text-neutral-500 py-6">No transactions in this bucket yet</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function MembershipsTab() {
  const [rows, setRows] = useState([]);
  useEffect(() => { axios.get(`${A}/memberships`, { withCredentials: true }).then(r => setRows(r.data.memberships)); }, []);
  return (
    <div data-testid="admin-memberships" className="rounded-xl border border-white/10 overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-white/5 text-neutral-400 uppercase text-[10px] tracking-widest">
          <tr><th className="text-left px-3 py-2">Member</th><th className="text-left px-3 py-2">Tier</th><th className="text-left px-3 py-2">Started</th><th className="text-left px-3 py-2">Expires</th><th className="text-left px-3 py-2">Stripe</th></tr>
        </thead>
        <tbody>
          {rows.map((m, i) => (
            <tr key={i} className="border-t border-white/5">
              <td className="px-3 py-2"><div className="font-semibold">{m.name || '—'}</div><div className="text-[11px] text-neutral-500">{m.email}</div></td>
              <td className="px-3 py-2 capitalize"><Badge className={m.membership_tier === 'pro' ? 'bg-violet-500/20 text-violet-300' : 'bg-amber-500/20 text-amber-300'}>{m.membership_tier}</Badge></td>
              <td className="px-3 py-2 text-[11px] text-neutral-500">{fmtDate(m.membership_started_at)}</td>
              <td className="px-3 py-2 text-[11px] text-neutral-500">{fmtDate(m.membership_expires_at)}</td>
              <td className="px-3 py-2 text-[11px]">{m.stripe_customer_id ? <a href={`https://dashboard.stripe.com/customers/${m.stripe_customer_id}`} target="_blank" rel="noreferrer" className="text-lime-400 hover:underline inline-flex items-center gap-0.5">{m.stripe_customer_id.slice(0,10)}… <ExternalLink className="w-3 h-3" /></a> : '—'}</td>
            </tr>
          ))}
          {!rows.length && <tr><td colSpan={5} className="text-center text-neutral-500 py-6">No paying members yet</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function ClubsTab() {
  const [rows, setRows] = useState([]);
  useEffect(() => { axios.get(`${A}/clubs`, { withCredentials: true }).then(r => setRows(r.data.clubs)); }, []);
  return (
    <div data-testid="admin-clubs" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
      {rows.map((c, i) => (
        <Card key={i} className="bg-white/[0.03] border-white/10 text-white">
          <CardContent className="p-4">
            <div className="font-black">{c.name}</div>
            <div className="text-[11px] text-neutral-500 mt-1">{c.club_id}</div>
            <div className="grid grid-cols-3 gap-2 mt-3 text-xs">
              <div><div className="text-neutral-500">Members</div><div className="font-mono text-lime-400">{(c.members || []).length}</div></div>
              <div><div className="text-neutral-500">Amount</div><div className="font-mono">{$(c.contribution_amount)}</div></div>
              <div><div className="text-neutral-500">Status</div><div className="capitalize text-cyan-400">{c.status || 'active'}</div></div>
            </div>
          </CardContent>
        </Card>
      ))}
      {!rows.length && <div className="col-span-full text-center text-neutral-500 py-6">No clubs</div>}
    </div>
  );
}

function ModerationTab() {
  const [rows, setRows] = useState([]);
  const load = () => axios.get(`${A}/moderation-flags`, { withCredentials: true }).then(r => setRows(r.data.flags));
  useEffect(() => { load(); }, []);
  const act = async (id, kind) => {
    await axios.post(`${A}/moderation-flags/${id}/${kind}`, {}, { withCredentials: true });
    toast.success(kind === 'mark-reviewed' ? 'Marked reviewed' : 'Message unhidden');
    load();
  };
  return (
    <div data-testid="admin-moderation" className="space-y-2">
      {rows.map((f, i) => (
        <Card key={i} className="bg-white/[0.03] border-white/10 text-white">
          <CardContent className="p-4">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className={f.action === 'block' ? 'bg-rose-500/20 text-rose-300' : 'bg-amber-500/20 text-amber-300'}>{f.action}</Badge>
                  <span className="text-[11px] text-neutral-500">{f.verdict?.category} · risk {f.verdict?.fraud_risk}/100</span>
                  <span className="text-[11px] text-neutral-500">{fmtDate(f.created_at)}</span>
                </div>
                <div className="text-sm mt-2 text-neutral-200">“{f.content_preview}”</div>
                <div className="text-[11px] text-neutral-500 mt-1 italic">{f.verdict?.reason}</div>
              </div>
              <div className="flex flex-col gap-2">
                <Button size="sm" variant="outline" onClick={() => act(f.message_id, 'mark-reviewed')} className="border-white/10 text-white hover:bg-white/10">Mark reviewed</Button>
                {f.action === 'block' && <Button size="sm" variant="outline" onClick={() => act(f.message_id, 'unhide')} className="border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10">Unhide</Button>}
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
      {!rows.length && <div className="text-center text-neutral-500 py-6">Nothing to review — quiet is good.</div>}
    </div>
  );
}

function PlaidTab() {
  const [rows, setRows] = useState([]);
  useEffect(() => { axios.get(`${A}/plaid-connections`, { withCredentials: true }).then(r => setRows(r.data.connections)); }, []);
  return (
    <div data-testid="admin-plaid" className="rounded-xl border border-white/10 overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-white/5 text-neutral-400 uppercase text-[10px] tracking-widest"><tr><th className="text-left px-3 py-2">User</th><th className="text-left px-3 py-2">Bank</th><th className="text-left px-3 py-2">Mask</th><th className="text-left px-3 py-2">Status</th><th className="text-left px-3 py-2">Linked</th></tr></thead>
        <tbody>
          {rows.map((c, i) => (
            <tr key={i} className="border-t border-white/5">
              <td className="px-3 py-2 text-xs">{c.user_id}</td>
              <td className="px-3 py-2">{c.bank_name || c.institution_name || '—'}</td>
              <td className="px-3 py-2 font-mono text-xs">•••{c.account_mask || c.mask || '••'}</td>
              <td className="px-3 py-2"><Badge className={c.status === 'active' ? 'bg-emerald-500/20 text-emerald-300' : c.status === 'needs_reauth' ? 'bg-amber-500/20 text-amber-300' : 'bg-rose-500/20 text-rose-300'}>{c.status || 'active'}</Badge></td>
              <td className="px-3 py-2 text-[11px] text-neutral-500">{fmtDate(c.created_at)}</td>
            </tr>
          ))}
          {!rows.length && <tr><td colSpan={5} className="text-center text-neutral-500 py-6">No Plaid connections</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function AuditTab() {
  const [rows, setRows] = useState([]);
  useEffect(() => { axios.get(`${A}/audit-log`, { withCredentials: true }).then(r => setRows(r.data.entries)); }, []);
  return (
    <div data-testid="admin-audit" className="space-y-1">
      {rows.map((e, i) => (
        <div key={i} className="flex items-center gap-3 px-3 py-2 bg-white/[0.03] border border-white/5 rounded-lg text-xs">
          <span className="text-neutral-500 min-w-[140px]">{fmtDate(e.created_at)}</span>
          <span className="font-mono text-lime-400 min-w-[180px]">{e.action}</span>
          <span className="text-neutral-400 flex-1">{e.actor_email} → {e.target || '—'}</span>
          <span className="text-neutral-600 text-[10px]">{e.ip}</span>
        </div>
      ))}
      {!rows.length && <div className="text-center text-neutral-500 py-6">No admin actions yet</div>}
    </div>
  );
}
